import React, { useEffect, useRef, useState } from 'react';
import './AdminUsersEditPage.scss';
import { useTranslation } from 'react-i18next';
import Loader from '../../../../shared/components/common/loader/Loader';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { useForm } from 'react-hook-form';
import {
  addAdminNote,
  changeUserAccountData,
  deleteAdminNote,
  getAccountData,
  getInvitedUsers,
  getProjectsForAccount,
  impersonateUser,
  updateAdminNote,
} from '../../../../shared/services/admin/users/AdminUsersService';
import { AdminNoteResponse } from '../../../../shared/models/admin/users/AdminNoteResponse';
import { formatBilling } from '../../../../shared/helpers/admin/formatBilling';
import { getTokenData } from '../../../../shared/services/auth/AuthService';
import { IMPERSONATION_ORIGINAL_TOKEN_KEY } from '../../../../shared/components/impersonation-banner/ImpersonationBanner';
import { useAuth } from '../../../../shared/contexts/auth/AuthContext';
import { useAccount } from '../../../../shared/contexts/account/AccountContexts';
import { AccountDataResponse } from '../../../../shared/models/account/AccountData';
import { ConfirmDialog, confirmDialog, ConfirmDialogReturn } from 'primereact/confirmdialog';
import { InvitedUserResponse } from '../../../../shared/models/admin/users/InvitedUserResponse';
import { format } from 'date-fns';
import AdminInviteUser from '../components/admin-invite-user/AdminInviteUser';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { PROJECTS } from '../../../../shared/routes/path';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import DropdownItem from '../../../../shared/components/dropdown-item/DropdownItem';
import { AccountType } from '../../../../shared/types/account/AccountType';
import { AccountStatus } from '../../../../shared/types/account/AccountStatus';
import { UserAccountData } from './models/UserAccountData';
import { Checkbox } from 'primereact/checkbox';
import { allMonitorTypes } from '../../../../shared/types/monitor/MonitorType';
import { allAddons } from '../../../../shared/types/account/Addons';
import { ApiKeyResponse, CreateApiKeyRequest } from '../../../settings/api-keys/models/ApiKeyResponse';
import { createAccountApiKey, getAccountApiKeys } from './services/AdminAccountApiKeysService';
import EmptyPanel from '../../../../shared/components/empty-panel/EmptyPanel';

const AdminUsersEditPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { accountResponse, refetchData } = useAccount();
  const [invitedUser, setInvitedUser] = useState<InvitedUserResponse[] | null>(null);
  const [userData, setUserData] = useState<AccountDataResponse | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [range, setRange] = useState<Date[] | null>(null);
  const [accountStatus, setAccountStatus] = useState<AccountStatus | null>(null);
  const [accountType, setAccountType] = useState<AccountType | null>(null);
  const [isCustomSettings, setCustomSettings] = useState<boolean>(false);
  const [selectedAddons, setSelectedAddons] = useState<string[]>([]);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [paymentSuspended, setPaymentSuspended] = useState<boolean>(false);
  const [hasBrandAccount, setHasBrandAccount] = useState<boolean>(false);
  const [apiKeys, setApiKeys] = useState<ApiKeyResponse[] | null>(null);
  const [showCopiedNotification, setShowCopiedNotification] = useState(false);
  const [projectsForId, setProjectsForId] = useState<{ id: number; label: string; created: string }[]>([]);
  const [adminNotes, setAdminNotes] = useState<AdminNoteResponse[]>([]);
  const [newNoteContent, setNewNoteContent] = useState<string>('');
  const [isAddingNote, setIsAddingNote] = useState<boolean>(false);
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [editingContent, setEditingContent] = useState<string>('');
  const [isSavingEdit, setIsSavingEdit] = useState<boolean>(false);
  const [showAllNotes, setShowAllNotes] = useState<boolean>(false);
  const NOTES_INITIAL_VISIBLE = 5;
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  let confirmRef: ConfirmDialogReturn;
  const calendarRef = useRef<Calendar>(null);
  const accountStatusOpt: { name: string; type: AccountStatus }[] = [
    { name: 'Paid', type: 'PAID' },
    { name: 'Trial', type: 'TRIAL' },
    { name: 'Overdue', type: 'OVERDUE' },
    { name: 'Blocked', type: 'BLOCKED' },
    { name: 'Disabled', type: 'DISABLED' },
  ];
  const accountTypeOpt: { name: string; type: AccountType }[] = [
    { name: 'Trial', type: 'TRIAL' },
    { name: 'Standard', type: 'STANDARD' },
    { name: 'Pro', type: 'PRO' },
    { name: 'Advanced', type: 'ADVANCED' },
    { name: 'Enterprise', type: 'ENTERPRISE' },
  ];

  const clearApiError = () => {
    setApiError(null);
  };

  const successDialog = () => {
    confirmDialog({
      message: 'Data successfully saved',
      header: t('dataSaved'),
      defaultFocus: 'accept',
      acceptLabel: 'OK',
      className: 'dialog-box dialog-box--accept',
      accept: () => window.location.reload(),
    });
  };

  const hideDialog = () => {
    if (confirmRef) {
      confirmRef.hide();
    }

    window.location.reload();
  };
  const inviteUser = () => {
    confirmRef = confirmDialog({
      message: <AdminInviteUser accountId={Number(id)} afterSave={hideDialog} />,
      header: 'Invite user',
      defaultFocus: 'accept',
      acceptClassName: 'confirm-change-name-btn',
      footer: () => null,
    });
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const [accountResponse, invitedUserResponse, apiKeysResponse, projects] = await Promise.all([
          getAccountData(Number(id)),
          getInvitedUsers(Number(id)),
          getAccountApiKeys(Number(id)),
          getProjectsForAccount(Number(id)),
        ]);

        setProjectsForId(projects);

        setUserData(accountResponse);
        setInvitedUser(invitedUserResponse);
        setApiKeys(apiKeysResponse);
        setAccountStatus(accountResponse.accountStatus);
        setAccountType(accountResponse.accountType);
        setCustomSettings(accountResponse.isCustomSettings);
        setPaymentSuspended(accountResponse.paymentSuspended || false);
        setHasBrandAccount(accountResponse.hasBrandAccount || false);
        setSelectedAddons(accountResponse.addons || []);
        setSelectedFeatures(accountResponse.features || []);
        setAdminNotes(accountResponse.adminNotes ?? []);

        const startDate = new Date(accountResponse.created);
        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + accountResponse.trialDays);
        setRange([startDate, endDate]);
      } catch (error) {
        console.error('Failed to load account data:', error);
      }
    };

    loadData();
  }, [id]);

  useEffect(() => {
    if (showCopiedNotification) {
      const timer = setTimeout(() => {
        setShowCopiedNotification(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showCopiedNotification]);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setShowCopiedNotification(true);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const loadAccountApiKeys = async () => {
    try {
      const apiKeysResponse = await getAccountApiKeys(Number(id));
      setApiKeys(apiKeysResponse);
    } catch (error) {
      console.error('Failed to load API keys for account:', error);
    }
  };

  const getKeyStatus = (expires: Date | null | undefined) => {
    if (!expires) {
      return { status: 'active', label: t('neverExpires') };
    }

    const now = new Date();
    const expiresDate = new Date(expires);
    const daysUntilExpiry = Math.ceil((expiresDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (now > expiresDate) {
      return { status: 'expired', label: t('expired') };
    } else if (daysUntilExpiry <= 7) {
      return { status: 'expires-soon', label: t('expiresInDays', { days: daysUntilExpiry }) };
    } else {
      return { status: 'active', label: t('expiresInDays', { days: daysUntilExpiry }) };
    }
  };

  const addNewAccountApiKey = () => {
    confirmRef = confirmDialog({
      message: (
        <AdminApiKeyCreateForm
          accountId={Number(id)}
          afterSave={async () => {
            if (confirmRef) {
              confirmRef.hide();
            }
            await loadAccountApiKeys();
          }}
        />
      ),
      header: t('createApiKey'),
      defaultFocus: 'accept',
      acceptClassName: 'confirm-change-name-btn',
      footer: () => null,
    });
  };

  const AdminApiKeyCreateForm: React.FC<{ accountId: number; afterSave: () => void }> = ({
    accountId,
    afterSave,
  }) => {
    const [localApiError, setLocalApiError] = useState<string | null>(null);
    const {
      register: registerApiKey,
      handleSubmit: handleSubmitApiKey,
      formState: { errors: apiKeyErrors },
    } = useForm();

    const validityOptions = [
      { label: t('never'), value: undefined },
      { label: t('30Days'), value: 30 },
      { label: t('90Days'), value: 90 },
      { label: t('180Days'), value: 180 },
      { label: t('365Days'), value: 365 },
    ];

    const onSubmitApiKey = async (formData: any) => {
      try {
        setLocalApiError(null);
        const request: CreateApiKeyRequest = {
          validForDays: formData.validForDays ? parseInt(formData.validForDays, 10) : undefined,
          description: formData.description || undefined,
        };
        await createAccountApiKey(accountId, request);
        afterSave();
      } catch (error: any) {
        console.error('Failed to create API key for account:', error);
        setLocalApiError(error.response?.data?.error || t('createApiKeyError'));
      }
    };

    const clearLocalApiError = () => {
      setLocalApiError(null);
    };

    return (
      <div>
        <form onSubmit={handleSubmitApiKey(onSubmitApiKey)} className="form-box__form">
          <div className="form-box__row">
            <div className="form-box__label">{t('description')}:</div>
            <InputText
              onInput={clearLocalApiError}
              {...registerApiKey('description')}
              placeholder={t('enterDescription')}
              className={apiKeyErrors.description || localApiError ? 'form-box__invalid' : ''}
            />
            {apiKeyErrors.description && <span>{apiKeyErrors.description.message as string}</span>}
          </div>

          <div className="form-box__row">
            <div className="form-box__label">{t('validityPeriod')}:</div>
            <Dropdown
              {...registerApiKey('validForDays')}
              options={validityOptions}
              placeholder={t('selectValidityPeriod')}
              className="dropdown"
              optionLabel="label"
              optionValue="value"
            />
          </div>

          {localApiError && <div className="p-error">{localApiError}</div>}

          <Button className="btn btn--sm" label={t('create')} type="submit" />
        </form>
      </div>
    );
  };

  const handleAddNote = async () => {
    if (!id) return;
    const trimmed = newNoteContent.trim();
    if (trimmed.length === 0) return;
    setIsAddingNote(true);
    try {
      const created = await addAdminNote(Number(id), trimmed);
      setAdminNotes((prev) => [created, ...prev]);
      setNewNoteContent('');
    } catch (error) {
      console.error('Failed to add admin note', error);
    } finally {
      setIsAddingNote(false);
    }
  };

  const handleStartEditNote = (note: AdminNoteResponse) => {
    setEditingNoteId(note.id);
    setEditingContent(note.content);
  };

  const handleCancelEditNote = () => {
    setEditingNoteId(null);
    setEditingContent('');
  };

  const handleSaveEditNote = async () => {
    if (!id || editingNoteId == null) return;
    const trimmed = editingContent.trim();
    if (trimmed.length === 0) return;
    setIsSavingEdit(true);
    try {
      const updated = await updateAdminNote(Number(id), editingNoteId, trimmed);
      setAdminNotes((prev) => prev.map((n) => (n.id === editingNoteId ? updated : n)));
      setEditingNoteId(null);
      setEditingContent('');
    } catch (error) {
      console.error('Failed to update admin note', error);
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleDeleteNote = async (noteId: number) => {
    if (!id) return;
    // Snapshot the row by id, not the whole array — if another note is added
    // mid-flight, a wholesale array rollback would drop it.
    const snapshot = adminNotes.find((n) => n.id === noteId);
    setAdminNotes((prev) => prev.filter((n) => n.id !== noteId));
    try {
      await deleteAdminNote(Number(id), noteId);
    } catch (error) {
      console.error('Failed to delete admin note', error);
      if (snapshot) {
        setAdminNotes((curr) => (curr.some((n) => n.id === noteId) ? curr : [...curr, snapshot]));
      }
    }
  };

  const handleOpenPaddle = () => {
    if (!userData?.subscriptionId) return;
    window.open(
      `https://vendors.paddle.com/subscriptions-v2/${encodeURIComponent(userData.subscriptionId)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handleOpenIntercom = () => {
    if (!userData?.intercomUrl) return;
    window.open(userData.intercomUrl, '_blank', 'noopener,noreferrer');
  };

  const handleImpersonate = async () => {
    if (!id) return;
    try {
      const originalTokenData = getTokenData();
      if (originalTokenData) {
        sessionStorage.setItem(IMPERSONATION_ORIGINAL_TOKEN_KEY, JSON.stringify(originalTokenData));
      }
      const tokenData = await impersonateUser(Number(id));
      login(tokenData);
      refetchData();
      navigate(PROJECTS);
    } catch (error) {
      console.error('Failed to impersonate user', error);
    }
  };

  const onSubmit = async (formData: any) => {
    const userAccount = {
      accountId: Number(id),
      questionLimit: Number(formData.questionLimit),
      email: formData.email,
      firstName: formData.firstName,
      lastName: formData.lastName,
      trialDays: Number(daysBetween),
      maxUsers: Number(formData.maxUsers),
      maxGeoReportsPerMonth: formData.maxGeoReportsPerMonth ? Number(formData.maxGeoReportsPerMonth) : null,
      accountStatus,
      accountType,
      discountId: formData.discountId,
      isCustomSettings,
      paymentSuspended,
      hasBrandAccount,
      addons: selectedAddons,
      features: selectedFeatures,
    } as UserAccountData;

    if (
      isNaN(daysBetween) ||
      isNaN(userAccount.questionLimit) ||
      isNaN(userAccount.maxUsers) ||
      isNaN(userAccount.accountId)
    ) {
      setApiError('Wrong data');
      return;
    }

    await changeUserAccountData(userAccount)
      .then(() => {
        successDialog();
      })
      .catch((err) => {
        if (err.statusCode === 401) {
          return;
        }

        setApiError(err.response.data.error);
      });
  };

  if (!projectsForId || !userData || invitedUser === null || !range || !apiKeys) {
    return (
      <div className="admin-edit-user">
        <Loader />
      </div>
    );
  }

  const daysBetween =
    range[0] && range[1]
      ? Math.max(1, Math.ceil((range[1].getTime() - range[0].getTime()) / (1000 * 60 * 60 * 24)))
      : 0;

  return (
    <div className="admin-edit-user">
      <ConfirmDialog className="dialog-box" />

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="page-box__title-action">
          <div className="page-box__title">
            Account data
            <div className="admin-edit-user__header-actions">
              {accountResponse?.isSuperAdmin && Number(id) !== accountResponse?.accountId && (
                <Button
                  className="btn btn--sm"
                  label={t('loginAsUser')}
                  type="button"
                  onClick={handleImpersonate}
                />
              )}
              {userData.intercomUrl && (
                <Button
                  className="btn btn--sm"
                  label="Open in Intercom"
                  type="button"
                  onClick={handleOpenIntercom}
                />
              )}
            </div>
          </div>
        </div>
        <div className="form">
          <div className="form__row">
            <div className="form-box__col">
              <div className="form-box__label">Email:</div>
              <InputText
                onInput={clearApiError}
                defaultValue={String(userData.email)}
                {...register('email', { required: t('fieldIsRequired') })}
                className={errors.email || apiError ? 'form-box__invalid' : ''}
              />
              {errors.email && <span>{errors.email.message as string}</span>}
            </div>
          </div>
          <div className="form__row">
            <div className="form-box__col">
              <div className="form-box__label">First name:</div>
              <InputText
                onInput={clearApiError}
                defaultValue={String(userData.firstName)}
                {...register('firstName')}
                className={errors.firstName || apiError ? 'form-box__invalid' : ''}
              />
              {errors.firstName && <span>{errors.firstName.message as string}</span>}
            </div>
          </div>
          <div className="form__row">
            <div className="form-box__col">
              <div className="form-box__label">Last name:</div>
              <InputText
                onInput={clearApiError}
                defaultValue={String(userData.lastName)}
                {...register('lastName')}
                className={errors.lastName || apiError ? 'form-box__invalid' : ''}
              />
              {errors.lastName && <span>{errors.lastName.message as string}</span>}
            </div>
          </div>
          <div className="form__row">
            <div className="form-box__col">
              <div className="form-box__label">Discount id:</div>
              <InputText
                onInput={clearApiError}
                {...register('discountId')}
                defaultValue={userData.discountId || ''}
                className={apiError ? 'form-box__invalid' : ''}
              />
            </div>
          </div>
          <div className="form__row">
            <div className="form-box__col">
              <div className="form-box__checkbox">
                <Checkbox
                  onChange={() => setHasBrandAccount(!hasBrandAccount)}
                  checked={hasBrandAccount}
                />
                <span>Has Brand24 account</span>
              </div>
            </div>
          </div>
          <div className="form__row">
            <div className="form-box__col">
              <div className="form-box__label">Account status:</div>
              <Dropdown
                onChange={(e) => {
                  setAccountStatus(e.value.type);
                }}
                value={accountStatusOpt.find((o) => o.type === accountStatus)}
                className="dropdown dropdown--disabled"
                optionLabel="name"
                options={accountStatusOpt}
                itemTemplate={(option) => <DropdownItem name={option.name} />}
              />
              <div className="legend">
                PAID (aktywne płacące)
                <br />
                TRIAL (aktywne trial)
                <br />
                OVERDUE (przekroczony termin zapłaty działa normalnie)
                <br />
                BLOCKED (przekroczony termin user nie może nic zrobić, w tle działa normalnie )<br />
                DISABLED (zablokowane)
              </div>
            </div>
          </div>
          <div className="form__row">
            <div className="form-box__col">
              <div className="form-box__label">Account type:</div>
              <Dropdown
                onChange={(e) => {
                  setAccountType(e.value.type);
                }}
                value={accountTypeOpt.find((o) => o.type === accountType)}
                options={accountTypeOpt}
                className="dropdown"
                optionLabel="name"
                itemTemplate={(option) => <DropdownItem name={option.name} />}
                disabled={true}
              />
              <div className="legend">
                TRIAL 20 questions / 2 users
                <br />
                STANDARD 10 questions / 1 user
                <br />
                PRO 30 questions / 2 users
                <br />
                ADVANCED 100 questions / 10 users
                <br />
                ENTERPRISE custom questions / custom users
              </div>
            </div>
          </div>
          <div className="form__row">
            <div className="form-box__col">
              <div className="form-box__label">Trial expiration:</div>
              <div className="calendar">
                <Calendar
                  ref={calendarRef}
                  value={range}
                  onChange={(e) => {
                    const endDate = (e.value as Date[])[1] || (e.value as Date[])[0];
                    setRange([new Date(userData.created), endDate]);
                    if (endDate && calendarRef.current) {
                      calendarRef.current.hide();
                    }
                  }}
                  selectionMode="range"
                  readOnlyInput
                  hideOnRangeSelection
                  dateFormat="yy-mm-dd"
                  pt={{
                    root: {
                      className: 'calendar',
                    },
                    panel: {
                      className: 'calendar-panel',
                    },
                  }}
                  minDate={new Date(userData.created)}
                />
              </div>
              <InputText
                value={String(daysBetween)}
                className={errors.trialDays || apiError ? 'form-box__invalid' : ''}
                disabled={true}
              />
            </div>
          </div>
          <div className="form__row">
            <div className="form-box__col">
              <div className="form-box__label">Question limit:</div>
              <InputText
                onInput={clearApiError}
                defaultValue={String(userData.maxMonitorCount)}
                {...register('questionLimit', { required: t('fieldIsRequired') })}
                className={errors.questionLimit || apiError ? 'form-box__invalid' : ''}
              />
            </div>
          </div>
          <div className="form__row">
            <div className="form-box__col">
              <div className="form-box__label">User limit:</div>
              <InputText
                onInput={clearApiError}
                defaultValue={String(userData.maxUsers)}
                {...register('maxUsers', { required: t('fieldIsRequired') })}
                className={errors.maxUsers || apiError ? 'form-box__invalid' : ''}
              />
              {errors.maxUsers && <span>{errors.maxUsers.message as string}</span>}
            </div>
          </div>
          <div className="form__row">
            <div className="form-box__col">
              <div className="form-box__label">GEO reports / month:</div>
              <InputText
                onInput={clearApiError}
                defaultValue={String(userData.maxGeoReportsPerMonth ?? '')}
                {...register('maxGeoReportsPerMonth')}
                className={apiError ? 'form-box__invalid' : ''}
                placeholder="Default (trial: 3, paid: 5)"
              />
            </div>
          </div>
          <div className="form__row">
            <div className="form-box__col">
              <div className="form-box__label">Account settings:</div>
              <div className="form-box__checkbox">
                <Checkbox
                  onChange={() => setCustomSettings(!isCustomSettings)}
                  checked={isCustomSettings}
                />
                <span>Custom settings</span>
              </div>
              <div className="form-box__checkbox">
                <Checkbox
                  onChange={() => setPaymentSuspended(!paymentSuspended)}
                  checked={paymentSuspended}
                />
                <span>{t('paymentSuspended')}</span>
              </div>
            </div>
          </div>
          <div className="form__row">
            <div className="form-box__col">
              <div className="form-box__label">Models:</div>
              {allMonitorTypes.map((model) => (
                <div className="form-box__checkbox">
                  <Checkbox
                    checked={selectedFeatures.includes(model)}
                    onChange={(e) => {
                      if (e.checked) {
                        setSelectedFeatures((prev) => [...prev, model]);
                      } else {
                        setSelectedFeatures((prev) => prev.filter((f) => f !== model));
                      }
                    }}
                  />
                  <span>{model}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="form__row">
            <div className="form-box__col">
              <div className="form-box__label">Addons:</div>
              {allAddons.map((addon) => (
                <div key={addon} className="form-box__checkbox">
                  <Checkbox
                    checked={selectedAddons.includes(addon)}
                    onChange={(e) => {
                      if (e.checked) {
                        setSelectedAddons((prev) => [...prev, addon]);
                      } else {
                        setSelectedAddons((prev) => prev.filter((f) => f !== addon));
                      }
                    }}
                  />
                  <span>{addon}</span>
                </div>
              ))}
            </div>
          </div>
          <Button className="btn btn--sm" label="Change data" type="submit" />
        </div>
      </form>
      <div className="admin-edit-user__right">
        <div className="admin-edit-user__account-summary">
          <div className="page-box__title-action">
            <div className="page-box__title">Subscription & limits</div>
          </div>
          <div className="admin-edit-user__summary-grid">
            <div className="admin-edit-user__summary-cell">
              <div className="admin-edit-user__summary-label">Account type</div>
              <div className="admin-edit-user__summary-value">{userData.accountType}</div>
            </div>
            <div className="admin-edit-user__summary-cell">
              <div className="admin-edit-user__summary-label">Billing cycle</div>
              <div className="admin-edit-user__summary-value">{formatBilling(userData.billingInterval)}</div>
            </div>
            <div className="admin-edit-user__summary-cell">
              <div className="admin-edit-user__summary-label">Discount</div>
              <div className="admin-edit-user__summary-value">
                {userData.discount && userData.discount !== '-' ? userData.discount : '-'}
                {userData.discountId ? ` (${userData.discountId})` : ''}
              </div>
            </div>
            <div className="admin-edit-user__summary-cell">
              <div className="admin-edit-user__summary-label">Created</div>
              <div className="admin-edit-user__summary-value">
                {userData.created ? format(new Date(userData.created), 'yyyy-MM-dd') : '-'}
              </div>
            </div>
            <div className="admin-edit-user__summary-cell">
              <div className="admin-edit-user__summary-label">End date</div>
              <div className="admin-edit-user__summary-value">
                {userData.endsAt ? format(new Date(userData.endsAt), 'yyyy-MM-dd') : '-'}
              </div>
            </div>
            <div className="admin-edit-user__summary-cell">
              <div className="admin-edit-user__summary-label">Last login</div>
              <div className="admin-edit-user__summary-value">
                {userData.lastLogin ? format(new Date(userData.lastLogin), 'yyyy-MM-dd') : '-'}
              </div>
            </div>
            <div className="admin-edit-user__summary-cell">
              <div className="admin-edit-user__summary-label">Login count</div>
              <div className="admin-edit-user__summary-value">{userData.loginCount ?? 0}</div>
            </div>
            <div className="admin-edit-user__summary-cell">
              <div className="admin-edit-user__summary-label">Projects</div>
              <div className="admin-edit-user__summary-value">{userData.projectsCount}</div>
            </div>
            <div className="admin-edit-user__summary-cell">
              <div className="admin-edit-user__summary-label">Prompts</div>
              <div className="admin-edit-user__summary-value">
                {(userData.accountType as string) === 'PAID'
                  ? `${userData.monitorCount} / unlimited`
                  : `${userData.monitorCount} / ${userData.maxMonitorCount}`}
              </div>
            </div>
            <div className="admin-edit-user__summary-cell">
              <div className="admin-edit-user__summary-label">Users</div>
              <div className="admin-edit-user__summary-value">{`${userData.usersCount} / ${userData.maxUsers}`}</div>
            </div>
          </div>
          {userData.subscriptionId && (
            <div className="admin-edit-user__summary-actions">
              <Button
                className="btn btn--sm"
                label="Open in Paddle"
                type="button"
                onClick={handleOpenPaddle}
              />
              <span className="admin-edit-user__subscription-id">{userData.subscriptionId}</span>
            </div>
          )}
        </div>

        <div className="admin-edit-user__admin-note">
          <div className="page-box__title-action">
            <div className="page-box__title">Admin notes ({adminNotes.length})</div>
          </div>

          <div className="admin-edit-user__admin-note-form">
            <textarea
              className="admin-edit-user__admin-note-textarea"
              value={newNoteContent}
              onChange={(e) => setNewNoteContent(e.target.value)}
              placeholder="Internal note visible to admins only"
              maxLength={5000}
              rows={2}
            />
            <div className="admin-edit-user__admin-note-actions">
              <Button
                className="btn btn--sm"
                label={isAddingNote ? 'Adding…' : 'Add note'}
                type="button"
                disabled={isAddingNote || newNoteContent.trim().length === 0}
                onClick={handleAddNote}
              />
            </div>
          </div>

          {adminNotes.length === 0 ? (
            <div className="admin-edit-user__admin-note-empty">No notes yet.</div>
          ) : (
            <>
              <ul className="admin-edit-user__admin-note-list">
                {(showAllNotes ? adminNotes : adminNotes.slice(0, NOTES_INITIAL_VISIBLE))
                  .map((note) => {
                    const isEditing = editingNoteId === note.id;
                    return (
                      <li key={note.id} className="admin-edit-user__admin-note-item">
                        <div className="admin-edit-user__admin-note-meta">
                          <span className="admin-edit-user__admin-note-author">
                            {note.authorEmail || 'unknown'}
                          </span>
                          <span className="admin-edit-user__admin-note-date">
                            {format(new Date(note.created), 'yyyy-MM-dd HH:mm')}
                            {note.updatedAt && (
                              <span className="admin-edit-user__admin-note-edited"> · edited {format(new Date(note.updatedAt), 'yyyy-MM-dd HH:mm')}</span>
                            )}
                          </span>
                          {!isEditing && (
                            <div className="admin-edit-user__admin-note-row-actions">
                              <button
                                type="button"
                                className="action-btn"
                                title="Edit"
                                onClick={() => handleStartEditNote(note)}
                              >
                                <span className="ico--edit ico--xs" />
                              </button>
                              <button
                                type="button"
                                className="action-btn"
                                title="Delete"
                                onClick={() => handleDeleteNote(note.id)}
                              >
                                <span className="ico--trash ico--xs" />
                              </button>
                            </div>
                          )}
                        </div>
                        {isEditing ? (
                          <div className="admin-edit-user__admin-note-edit">
                            <textarea
                              className="admin-edit-user__admin-note-textarea"
                              value={editingContent}
                              onChange={(e) => setEditingContent(e.target.value)}
                              maxLength={5000}
                              rows={3}
                              autoFocus
                            />
                            <div className="admin-edit-user__admin-note-edit-actions">
                              <Button
                                className="btn btn--sm btn--secondary"
                                label="Cancel"
                                type="button"
                                onClick={handleCancelEditNote}
                                disabled={isSavingEdit}
                              />
                              <Button
                                className="btn btn--sm"
                                label={isSavingEdit ? 'Saving…' : 'Save'}
                                type="button"
                                onClick={handleSaveEditNote}
                                disabled={
                                  isSavingEdit ||
                                  editingContent.trim().length === 0 ||
                                  editingContent === note.content
                                }
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="admin-edit-user__admin-note-content">{note.content}</div>
                        )}
                      </li>
                    );
                  })}
              </ul>
              {!showAllNotes && adminNotes.length > NOTES_INITIAL_VISIBLE && (
                <div className="admin-edit-user__admin-note-show-more">
                  <button
                    type="button"
                    className="admin-edit-user__admin-note-show-more-btn"
                    onClick={() => setShowAllNotes(true)}
                  >
                    Show more ({adminNotes.length - NOTES_INITIAL_VISIBLE})
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="admin-edit-user__accounts">
          <div className="page-box__title-action">
            <div className="page-box__title">
              Invited users
              <Button className="btn btn--sm" label="Add user" onClick={() => inviteUser()} />
            </div>
          </div>
          <div className="admin-edit-user__invited invited-table">
            {invitedUser.length > 0 ? (
              <>
                <div className="invited-table__row invited-table__row--header">
                  <div>User ID</div>
                  <div>Email</div>
                  <div>Role</div>
                  <div>Date</div>
                </div>
                {invitedUser.map((user) => (
                  <div className="invited-table__row" key={user.userId}>
                    <div>{user.userId}</div>
                    <div>{user.email}</div>
                    <div>{user.role}</div>
                    <div>{format(user.created, 'yyy-MM-dd')}</div>
                  </div>
                ))}
              </>
            ) : (
              <EmptyPanel />
            )}
          </div>
        </div>
        <div>
          <div className="page-box__title-action">
            <div className="page-box__title">Projects</div>
          </div>
          <DataTable
            stripedRows
            paginator
            paginatorClassName="pagination"
            removableSort
            sortField="id"
            sortOrder={1}
            rows={50}
            rowsPerPageOptions={[10, 25, 50]}
            value={projectsForId}
            tableStyle={{ width: '100%' }}
            tableClassName="table"
            className="table-wrapper"
            emptyMessage={() => <EmptyPanel />}
            currentPageReportTemplate={t('rowsNumber') + ':'}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          >
            <Column
              field="id"
              header="Project ID"
              headerClassName="table__header table__header--center"
              bodyClassName="table__cell--center"
              style={{ width: '45px' }}
            />
            <Column
              field="projectLabel"
              header="Name"
              body={(data) => (
                <NavLink to={`${PROJECTS}/${data.id}`} target="_blank">
                  {data.label}
                </NavLink>
              )}
              headerClassName="table__header"
              style={{ width: '100px' }}
            />
            <Column
              field="created"
              header="Created"
              body={(data) => format(data.created, 'yyyy-MM-dd / HH:mm')}
              headerClassName="table__header table__header--center"
              bodyClassName="table__cell--center"
              style={{ width: '145px' }}
            />
          </DataTable>
        </div>
        <div>
          <div className="page-box__title-action">
            <div className="page-box__title">
              {t('apiKeys')}
              <Button
                className="btn btn--sm"
                label={t('createApiKey')}
                onClick={() => addNewAccountApiKey()}
              />
            </div>
          </div>
          <DataTable
            stripedRows
            paginator
            paginatorClassName="pagination"
            removableSort
            sortField="created"
            sortOrder={-1}
            rows={50}
            rowsPerPageOptions={[10, 25, 50]}
            value={apiKeys}
            tableStyle={{ width: '100%' }}
            tableClassName="table"
            className="table-wrapper"
            emptyMessage={() => <EmptyPanel />}
            currentPageReportTemplate={t('rowsNumber') + ':'}
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          >
            <Column
              field="key"
              header={t('apiKey')}
              body={(apiKey: ApiKeyResponse) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                    {apiKey.key.substring(0, 20)}...
                  </span>
                  <Button
                    className="action-btn"
                    onClick={() => copyToClipboard(apiKey.key)}
                    title={t('copy')}
                  >
                    <span className="ico--copy ico--xs" />
                  </Button>
                </div>
              )}
              headerClassName="table__header"
              style={{ minWidth: '100px' }}
            />
            <Column
              field="description"
              header={t('description')}
              body={(apiKey: ApiKeyResponse) => apiKey.description || '-'}
              headerClassName="table__header"
            />
            <Column
              field="status"
              header={t('status')}
              body={(apiKey: ApiKeyResponse) => (
                <span className={`status-${apiKey.status === 'ACTIVE' ? 'active' : 'inactive'}`}>
                  {apiKey.status === 'ACTIVE' ? t('active') : t('inactive')}
                </span>
              )}
              headerClassName="table__header"
              style={{ minWidth: '50px' }}
            />
            <Column
              field="expires"
              header={t('validity')}
              sortable
              body={(apiKey: ApiKeyResponse) => {
                const { status, label } = getKeyStatus(apiKey.expires);
                return <span className={`status-${status}`}>{label}</span>;
              }}
              headerClassName="table__header"
              style={{ minWidth: '120px' }}
            />
          </DataTable>
          {showCopiedNotification && <div className="toast">{t('copied')}</div>}
        </div>
        <div>
          {/* API Integration Info */}
          {(userData.apiClientId ||
            userData.apiExternalAccountId ||
            userData.apiExternalAccountSettings) && (
            <div>
              <div className="page-box__title-action">
                <div className="page-box__title">{t('apiIntegration')}</div>
              </div>

              {userData.apiClientId && (
                <div className="form__row">
                  <div className="form-box__col">
                    <div className="form-box__label">{t('apiClientName')}:</div>
                    <div className="form-box__value">
                      {userData.apiClientName || `ID: ${userData.apiClientId}`}
                    </div>
                  </div>
                </div>
              )}

              {userData.apiExternalAccountId && (
                <div className="form__row">
                  <div className="form-box__col">
                    <div className="form-box__label">{t('apiExternalAccountId')}:</div>
                    <div className="form-box__value">{userData.apiExternalAccountId}</div>
                  </div>
                </div>
              )}

              {userData.apiExternalAccountSettings && (
                <div className="form__row">
                  <div className="form-box__col">
                    <div className="form-box__label">{t('apiExternalAccountSettings')}:</div>
                    <pre className="form-box__value form-box__pre">
                      {JSON.stringify(userData.apiExternalAccountSettings, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminUsersEditPage;
